import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import apiRequest from '../../lib/http'

export const getEnrollmentConfig = createAsyncThunk(
  'selfEnrollment/getConfig',
  async ({ affiliateId, selectedGroupId = null }, { rejectWithValue }) => {
    try {
      const query = selectedGroupId ? `?office=${encodeURIComponent(selectedGroupId)}` : ''
      const response = await apiRequest.get(`/enroll/${affiliateId}${query}`)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Invalid enrollment link'
      return rejectWithValue(errorMessage)
    }
  }
)

export const createEnrollmentContact = createAsyncThunk(
  'selfEnrollment/createContact',
  async ({ affiliateId, personalInfo, coApplicantInfo, selectedGroupId = null }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post(`/enroll/${affiliateId}/contact`, {
        personalInfo,
        coApplicantInfo,
        selectedGroupId,
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to create contact'
      return rejectWithValue(errorMessage)
    }
  }
)

export const updateEnrollmentContact = createAsyncThunk(
  'selfEnrollment/updateContact',
  async ({ contactId, personalInfo }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.put(`/enroll/contact/${contactId}`, { personalInfo })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to update contact'
      return rejectWithValue(errorMessage)
    }
  }
)

export const runCreditCheck = createAsyncThunk(
  'selfEnrollment/runCreditCheck',
  async ({ contactId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post(`/enroll/credit-check/${contactId}`)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Credit check failed'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getEnrollmentDebts = createAsyncThunk(
  'selfEnrollment/getDebts',
  async ({ contactId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get(`/enroll/debts/${contactId}`)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to get debts'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getCreditOptions = createAsyncThunk(
  'selfEnrollment/getCreditOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/enroll/credit-options')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to get credit options'
      return rejectWithValue(errorMessage)
    }
  }
)

export const createCreditReport = createAsyncThunk(
  'selfEnrollment/createCreditReport',
  async ({ contactId, fullName, reportDate, creditScore }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post(`/enroll/credit-report/${contactId}`, {
        fullName,
        reportDate,
        creditScore,
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to create credit report'
      return rejectWithValue(errorMessage)
    }
  }
)

export const createCreditProfiles = createAsyncThunk(
  'selfEnrollment/createCreditProfiles',
  async ({ contactId, creditReportId, profiles }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post(`/enroll/credit-profiles/${contactId}`, {
        creditReportId,
        profiles,
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to create credit profiles'
      return rejectWithValue(errorMessage)
    }
  }
)

export const updateEnrollmentDebt = createAsyncThunk(
  'selfEnrollment/updateDebt',
  async ({ contactId, debtId, data }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.put(`/enroll/credit-profile/${contactId}/${debtId}`, data)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to update debt'
      return rejectWithValue(errorMessage)
    }
  }
)

export const deleteEnrollmentDebt = createAsyncThunk(
  'selfEnrollment/deleteDebt',
  async ({ contactId, debtId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.delete(`/enroll/credit-profile/${contactId}/${debtId}`)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to delete debt'
      return rejectWithValue(errorMessage)
    }
  }
)

export const completeEnrollment = createAsyncThunk(
  'selfEnrollment/complete',
  async ({ contactId, selectedDebtIds, selectedTerm, frequency, startDate, creditType }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post(`/enroll/complete/${contactId}`, {
        selectedDebtIds,
        selectedTerm,
        frequency,
        startDate,
        creditType,
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Enrollment completion failed'
      return rejectWithValue(errorMessage)
    }
  }
)

const initialState = {
  config: null,
  contactId: null,
  debts: {
    eligible: [],
    ineligible: [],
  },
  creditOptions: {
    types: [],
    statuses: [],
  },
  creditReportId: null,
  loading: false,
  debtsLoading: false,
  creditCheckLoading: false,
  creditOptionsLoading: false,
  savingCreditReport: false,
  savingCreditProfiles: false,
  error: null,
  submitting: false,
  submitResult: null,
}

const selfEnrollmentSlice = createSlice({
  name: 'selfEnrollment',
  initialState,
  reducers: {
    resetEnrollment: () => {
      return initialState
    },
    setContactId: (state, action) => {
      state.contactId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEnrollmentConfig.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getEnrollmentConfig.fulfilled, (state, action) => {
        state.loading = false
        state.config = action.payload.data
      })
      .addCase(getEnrollmentConfig.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(createEnrollmentContact.pending, (state) => {
        state.submitting = true
        state.error = null
      })
      .addCase(createEnrollmentContact.fulfilled, (state, action) => {
        state.submitting = false
        state.contactId = action.payload.data.contactId
      })
      .addCase(createEnrollmentContact.rejected, (state, action) => {
        state.submitting = false
        state.error = action.payload
      })
      .addCase(updateEnrollmentContact.pending, (state) => {
        state.submitting = true
        state.error = null
      })
      .addCase(updateEnrollmentContact.fulfilled, (state) => {
        state.submitting = false
      })
      .addCase(updateEnrollmentContact.rejected, (state, action) => {
        state.submitting = false
        state.error = action.payload
      })
      .addCase(runCreditCheck.pending, (state) => {
        state.creditCheckLoading = true
        state.error = null
      })
      .addCase(runCreditCheck.fulfilled, (state, action) => {
        state.creditCheckLoading = false
        state.debts = action.payload.data
      })
      .addCase(runCreditCheck.rejected, (state, action) => {
        state.creditCheckLoading = false
        state.error = action.payload
      })
      .addCase(getEnrollmentDebts.pending, (state) => {
        state.debtsLoading = true
        state.error = null
      })
      .addCase(getEnrollmentDebts.fulfilled, (state, action) => {
        state.debtsLoading = false
        state.debts = action.payload.data
      })
      .addCase(getEnrollmentDebts.rejected, (state, action) => {
        state.debtsLoading = false
        state.error = action.payload
      })
      .addCase(completeEnrollment.pending, (state) => {
        state.submitting = true
        state.error = null
      })
      .addCase(completeEnrollment.fulfilled, (state, action) => {
        state.submitting = false
        state.submitResult = action.payload.data
      })
      .addCase(completeEnrollment.rejected, (state, action) => {
        state.submitting = false
        state.error = action.payload
      })
      .addCase(getCreditOptions.pending, (state) => {
        state.creditOptionsLoading = true
        state.error = null
      })
      .addCase(getCreditOptions.fulfilled, (state, action) => {
        state.creditOptionsLoading = false
        state.creditOptions = action.payload.data
      })
      .addCase(getCreditOptions.rejected, (state, action) => {
        state.creditOptionsLoading = false
        state.error = action.payload
      })
      .addCase(createCreditReport.pending, (state) => {
        state.savingCreditReport = true
        state.error = null
      })
      .addCase(createCreditReport.fulfilled, (state, action) => {
        state.savingCreditReport = false
        state.creditReportId = action.payload.data.creditReportId
      })
      .addCase(createCreditReport.rejected, (state, action) => {
        state.savingCreditReport = false
        state.error = action.payload
      })
      .addCase(createCreditProfiles.pending, (state) => {
        state.savingCreditProfiles = true
        state.error = null
      })
      .addCase(createCreditProfiles.fulfilled, (state, action) => {
        state.savingCreditProfiles = false
        state.debts = action.payload.data
      })
      .addCase(createCreditProfiles.rejected, (state, action) => {
        state.savingCreditProfiles = false
        state.error = action.payload
      })
      .addCase(updateEnrollmentDebt.fulfilled, (state, action) => {
        state.debts = action.payload.data
      })
      .addCase(updateEnrollmentDebt.rejected, (state, action) => {
        state.error = action.payload
      })
      .addCase(deleteEnrollmentDebt.fulfilled, (state, action) => {
        state.debts = action.payload.data
      })
      .addCase(deleteEnrollmentDebt.rejected, (state, action) => {
        state.error = action.payload
      })
  },
})

export const { resetEnrollment, setContactId } = selfEnrollmentSlice.actions
export default selfEnrollmentSlice.reducer
